import { initialize, LDClient, LDUser } from 'ldclient-js';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AuthContextTrace } from '../Authenticate/AuthenticateTrace';

export interface Features {
  'enable-builds-performance': boolean;
  'enable-crashes-distribution': boolean;
  'enable-crashes-session-filter': boolean;
  'enable-crashes': boolean;
  'enable-easter-egg-hammer-time': boolean;
  'enable-email-digest': boolean;
  'enable-metric-details': boolean;
  'enable-ongoing-maintenance-banner': boolean;
  'enable-overview-cold-startup-latency': boolean;
  'enable-overview': boolean;
  'enable-session-explorer-events': boolean;
  'enable-setup-android-bitrise-step': boolean;
  'enable-setup-android-gradle': boolean;
  'enable-setup-ios-bitrise-step': boolean;
  'enable-setup-ios-cocoa-pods': boolean;
  'enable-setup-ios-spm': boolean;
  'enable-setup-platform-switch': boolean;
}

export interface FeaturesInitialisePayload extends LDUser {
  accountId: string;
  appId: string;
  appTitle: string;
  key: string;
}

export type FeaturesCurrent = Record<keyof Features, { current: boolean }>;
export type FeatureCheck = (feature: keyof Features) => boolean;

type FeaturesInfo = {
  features: Features;
  isReady: boolean;
  hasFeature: FeatureCheck;
};

export const defaultFeatures: Features = {
  'enable-builds-performance': false,
  'enable-crashes-distribution': false,
  'enable-crashes-session-filter': false,
  'enable-crashes': false,
  'enable-easter-egg-hammer-time': false,
  'enable-email-digest': false,
  'enable-metric-details': false,
  'enable-ongoing-maintenance-banner': false,
  'enable-overview-cold-startup-latency': false,
  'enable-overview': false,
  'enable-session-explorer-events': false,
  'enable-setup-android-bitrise-step': true,
  'enable-setup-android-gradle': true,
  'enable-setup-ios-bitrise-step': true,
  'enable-setup-ios-cocoa-pods': true,
  'enable-setup-ios-spm': true,
  'enable-setup-platform-switch': false,
};

export const FeatureContext = createContext<FeaturesInfo>({
  features: defaultFeatures,
  hasFeature: () => false,
  isReady: false,
});

const useFeatures = (): FeaturesInfo => {
  const {
    data: { accountId, appId, appTitle },
  } = useContext(AuthContextTrace);
  const [isReady, setIsReady] = useState(false);
  const [features, setFeatures] = useState<Features>(defaultFeatures);

  const client = useRef<LDClient>();
  const hasFeature: FeatureCheck = (feature) => features[feature] || false;

  const setChangedFeatures = useCallback(
    (featuresUpdated: FeaturesCurrent) => {
      if (client.current) {
        setFeatures(
          Object.entries(featuresUpdated).reduce(
            (accFeatures, [feature, { current }]) => ({
              ...accFeatures,
              [feature]: current,
            }),
            features,
          ) as Features,
        );
      }
    },
    [features],
  );

  useEffect(() => {
    const setInitialFeatures = () => {
      if (client.current) {
        client.current.off('ready', setInitialFeatures);
        setFeatures(client.current.allFlags() as Features);
        setIsReady(true);
      }
    };

    if (accountId && appId && appTitle) {
      if (process.env.LAUNCH_DARKLY_CLIENT_ID) {
        const payload: FeaturesInitialisePayload = {
          accountId,
          appId,
          appTitle,
          name: appTitle,
          key: appId,
        };

        client.current = initialize(process.env.LAUNCH_DARKLY_CLIENT_ID, payload);
        client.current.on('ready', setInitialFeatures);
      }
    }

    return () => {
      if (client.current) {
        client.current.off('ready', setInitialFeatures);
      }
    };
  }, [accountId, appId, appTitle]);

  useEffect(() => {
    if (client.current) {
      client.current.off('change', setChangedFeatures);
      client.current.on('change', setChangedFeatures);
    }

    return () => {
      if (client.current) {
        client.current.off('change', setChangedFeatures);
      }
    };
  }, [setChangedFeatures]);

  return {
    features,
    hasFeature,
    isReady,
  };
};

export default useFeatures;
